'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { useRef } from 'react';

import {
  GTMEcommerceItem,
  GTMItemListName,
  GTMViewItemList,
  sendGTMEventEcommerce
} from '@/fo/api/gtm';
import {
  selectCountProductInCart,
  storeProduct,
  cartActions,
  wishlistActions,
  selectWishlistItemId,
  selectCartItemId,
  selectCartItemLoading
} from '@/fo/store/product';
import {
  UiProductTileSecondary,
  UiProductTileSecondaryProps
} from '@/fo/ui/product/tile/secondary';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';
import {
  MappedProduct,
  CartItemConfigurableOption
} from '@/fo/util/map-product-data';

export interface FeatureProductTileSecondaryProps {
  isAuthorized: boolean;
  data: MappedProduct;
  type: UiProductTileSecondaryProps['type'];
  showOldPrice?: boolean;
  className?: string;
  classNameActions?: string;
  isCart?: boolean;
  hideCartButton?: boolean;
  selectedAttributes?: CartItemConfigurableOption[];
  positionIndex?: number;
  viewItemListName?: GTMItemListName;
}

export function FeatureProductTileSecondary({
  isAuthorized,
  data,
  type,
  showOldPrice = false,
  className,
  classNameActions,
  isCart,
  selectedAttributes,
  positionIndex = 0,
  viewItemListName
}: FeatureProductTileSecondaryProps) {
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const wishlistSku = data.parentSku || data?.sku;
  const basePrice = data.basePrice || 0;
  const price = showOldPrice ? (data.finalPrice ?? basePrice) : basePrice;
  const oldPrice = showOldPrice
    ? data.finalPrice
      ? data.basePrice
      : null
    : null;

  const cartItemId = useSelector(storeProduct, selectCartItemId(data?.sku));
  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(data?.sku)
  );
  const cartLoading = useSelector(
    storeProduct,
    selectCartItemLoading(data?.sku)
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(wishlistSku)
  );

  const isInWishlist = Boolean(wishlistItemId);

  const brand = data.labelsBrand[0];

  const itemGtmData: GTMEcommerceItem = {
    id: data?.sku,
    name: data.name,
    price: data.itemPrice ?? price ?? 0,
    brand: data.brand ?? '',
    category:
      (data.lastLevelCategory?.breadcrumbs && data.lastLevelCategory?.name) ??
      data.brand ??
      '',
    position: positionIndex + 1,
    list: viewItemListName
  };

  const onClick = () => {
    sendGTMEventEcommerce({
      event: 'select_item',
      ecommerce: {
        products: [itemGtmData]
      }
    });
  };

  const onCartClick = () => {
    cartActions.addProductsToCart([
      {
        sku: data.sku,
        quantity: 1,
        product: data
      }
    ]);
  };

  const onCartChange = async (quantity: number) => {
    if (cartLoading) {
      return;
    }

    if (quantity === 0) {
      await cartActions.removeCartItem(data.sku, cartItemId, data);
    } else {
      await cartActions.updateCartItem(data.sku, cartItemId, quantity, data);
    }

    if (isCart) {
      utilEventCartModal.mutate();
    } else {
      router.refresh();
    }
  };

  const onWishlistClick = async (
    sku: string,
    wishlistSku: string,
    parentSku?: string,
    wishlistItemId?: string,
    isInWishlist?: boolean
  ) => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        await wishlistActions.removeWishlistItems([
          { sku: wishlistSku, wishlistItemId }
        ]);
      } else {
        await wishlistActions.addProductToWishlist(sku, 1, parentSku);
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onRemoveClick = async () => {
    if (cartLoading) {
      return;
    }

    await cartActions.removeCartItem(data.sku, cartItemId, data);

    if (isCart) {
      utilEventCartModal.mutate();
    } else {
      router.refresh();
    }
  };

  const getLink = () => {
    const isGiftOrSample =
      ['gift', 'sample'].includes(data?.productGroup?.name ?? '') ||
      type === 'gift' ||
      type === 'sample';

    if (isGiftOrSample && data?.catalogBrandUrl) {
      return data.catalogBrandUrl[0] === '/'
        ? data.catalogBrandUrl
        : `/${data.catalogBrandUrl}`;
    }
    return data.link;
  };

  const maxCount =
    data?.groupType && data?.items?.length
      ? Math.min(...data.items.map((item) => item.stockQuantity || 0))
      : data.stockQuantity;
  return (
    <>
      {viewItemListName && (
        <GTMViewItemList
          key={`gtm-${data.sku}`}
          refItem={ref}
          item={itemGtmData}
        />
      )}

      <UiProductTileSecondary
        key={data?.sku}
        className={className}
        classNameActions={classNameActions}
        isAuthorized={isAuthorized}
        type={type}
        bonuses={data.bonusPoints}
        bonusesMax={data.bonusPointsMax}
        link={basePrice > 0 ? getLink() : undefined}
        image={data.thumbnail?.src || ''}
        title={data.name}
        description={data.nameEng}
        price={price}
        oldPrice={oldPrice}
        currency={data.currency}
        brand={
          brand
            ? {
                color: brand.color,
                name: brand.name,
                url: brand.url
              }
            : undefined
        }
        count={
          type === 'order' && data.quantity ? data.quantity || 0 : cartCount
        }
        maxCount={maxCount > 0 ? maxCount : undefined}
        isInWishlist={isInWishlist}
        options={[]}
        onClick={onClick}
        onCartClick={isCart ? undefined : onCartClick}
        onCartChange={onCartChange}
        onWishlistClick={() => {
          onWishlistClick(
            data.sku,
            wishlistSku,
            data.parentSku,
            wishlistItemId,
            isInWishlist
          );
        }}
        onRemoveClick={onRemoveClick}
        groupType={data?.groupType}
        childrenItems={
          data?.groupType && data?.items?.length ? data.items : undefined
        }
        onWishlistChildrenClick={onWishlistClick}
        isCart={isCart}
        isCartLoading={cartLoading}
        mindboxId={data.mindboxId}
        selectedAttributes={selectedAttributes}
        mindboxBonuses={data?.mindboxBonuses ?? 0}
      />
    </>
  );
}
